import Image from "next/image"
import { NumberPad } from "./number-pad"

interface LoginCardProps {
  staffId: string
  error: string
  loading: boolean
  loadingMessage: string
  onNumberPress: (num: string) => void
  onBackspace: () => void
  onLogin: () => void
}

export function LoginCard({
  staffId,
  error,
  loading,
  loadingMessage,
  onNumberPress,
  onBackspace,
  onLogin,
}: LoginCardProps) {
  return (
    <div className="relative w-full md:w-1/2 min-h-dvh flex flex-col items-center justify-center px-6 py-10 bg-linear-to-br from-navy-700 via-navy-800 to-navy-900 overflow-hidden">
      {/* Subtle gold vignette */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(191,154,76,0.08),_transparent_65%)]"
      />

      {/* Brand mark for small screens where the branding pane is hidden */}
      <Image
        src="/images/cherish-logo.png"
        alt="Cherish at Central Park"
        width={150}
        height={90}
        className="relative md:hidden mb-6 brightness-0 invert"
        style={{ width: "150px", height: "auto" }}
        priority
      />

      <div className="relative w-full max-w-[420px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.25),0_8px_20px_rgba(0,0,0,0.15)] p-8 sm:p-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Gold accent bar top */}
        <div
          aria-hidden="true"
          className="absolute inset-x-10 top-0 h-0.5 bg-linear-to-r from-transparent via-gold-500 to-transparent rounded-full"
        />

        {/* Card header - CEO wireframe: "Enter ID Number" */}
        <div className="text-center mb-7">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-navy-700 mb-2">
            Welcome
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-navy-900 tracking-tight">
            Enter ID Number
          </h1>
        </div>

        {/* ID number input field */}
        <div
          className="h-16 bg-linear-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-xl flex items-center justify-center px-6 mb-5 shadow-inner"
          role="status"
          aria-live="polite"
          aria-label={`PIN code, ${staffId.length} digits entered`}
        >
          <span className="text-3xl font-mono tracking-[0.4em] text-navy-900 tabular-nums">
            {staffId || <span className="text-slate-400">- - - -</span>}
          </span>
        </div>

        {/* Error / loading feedback */}
        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-center animate-in fade-in slide-in-from-top-1 duration-200">
            <p className="text-sm text-red-700 font-semibold">{error}</p>
          </div>
        )}

        {loadingMessage && !error && (
          <div className="mb-4 flex items-center justify-center gap-2 text-navy-700">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-navy-700 border-t-transparent" />
            <span className="text-sm font-medium">{loadingMessage}</span>
          </div>
        )}

        {/* Number pad - CEO wireframe: 1-9 grid, then <, 0, LOGIN */}
        <NumberPad
          loading={loading}
          canLogin={staffId.length >= 4}
          onNumberPress={onNumberPress}
          onBackspace={onBackspace}
          onLogin={onLogin}
        />
      </div>

      {/* Bottom text - CEO wireframe requirement */}
      <p className="relative mt-6 text-white/70 text-sm font-medium tracking-wide">
        Log in using your ID number
      </p>

      {/* Version number at bottom — kept for PWA cache-support diagnosis */}
      <div className="absolute bottom-5 right-5">
        <span className="text-white/40 text-[10px] font-mono tracking-wider">v2.1.0</span>
      </div>
    </div>
  )
}
